/**
 * Defensive Web Storage access (storage can be unavailable or throw in private browsing), plus the
 * small pieces of sign-in state that must survive a full-page redirect to the hosted sign-in page.
 */
export const StorageKeys = {
  affiliateSlug: "affiliate.slug",
  authMethod: "auth.method",
  returnTo: "auth.returnTo",
  loginHint: "auth.loginHint",
  redirectError: "auth.redirectError",
  redirectStarts: "auth.redirectStarts",
} as const;

type StorageArea = "local" | "session";

function getStorage(area: StorageArea): Storage | null {
  try {
    return area === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStorage(area: StorageArea, key: string): string | null {
  try {
    return getStorage(area)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStorage(area: StorageArea, key: string, value: string): void {
  try {
    getStorage(area)?.setItem(key, value);
  } catch {
    // Storage full or blocked: these values are conveniences, never required.
  }
}

export function removeStorage(area: StorageArea, key: string): void {
  try {
    getStorage(area)?.removeItem(key);
  } catch {
    // Storage blocked: nothing to remove.
  }
}

function take(area: StorageArea, key: string): string | null {
  const value = readStorage(area, key);
  if (value !== null) {
    removeStorage(area, key);
  }
  return value;
}

/** Clears local and session storage but keeps the affiliate slug, so "sign in again" stays branded. */
export function clearBrowserStorage(): void {
  const affiliateSlug = readStorage("session", StorageKeys.affiliateSlug);

  try {
    getStorage("local")?.clear();
  } catch {
    // Storage blocked.
  }
  try {
    getStorage("session")?.clear();
  } catch {
    // Storage blocked.
  }

  if (affiliateSlug) {
    writeStorage("session", StorageKeys.affiliateSlug, affiliateSlug);
  }
}

/**
 * MSAL records an in-progress interaction in storage. An interrupted or failed redirect can leave it
 * set, and then every later loginRedirect throws interaction_in_progress before it can navigate.
 */
export function clearInteractionStatus(): void {
  for (const area of ["local", "session"] as const) {
    const storage = getStorage(area);
    if (!storage) continue;
    try {
      Object.keys(storage)
        .filter((key) => key.includes("interaction.status"))
        .forEach((key) => storage.removeItem(key));
    } catch {
      // Storage blocked.
    }
  }
}

export const saveReturnTo = (path: string) => writeStorage("session", StorageKeys.returnTo, path);
export const consumeReturnTo = () => take("session", StorageKeys.returnTo);

export const saveLoginHint = (hint: string) => writeStorage("session", StorageKeys.loginHint, hint);
export const consumeLoginHint = () => take("session", StorageKeys.loginHint);

/** Records only an error code (never a description) from a failed redirect sign-in. */
export function saveRedirectError(error: unknown): void {
  const errorCode = (error as { errorCode?: unknown } | null)?.errorCode;
  writeStorage(
    "session",
    StorageKeys.redirectError,
    typeof errorCode === "string" ? errorCode : "unknown_error"
  );
}

export const consumeRedirectError = () => take("session", StorageKeys.redirectError);
